// @ts-nocheck
import { ChatMessage } from '@/hooks/useChatMessages';
import { logger } from '@/utils/productionLogger';

interface CachedChatData {
  messages: ChatMessage[];
  lastSync: string;
  lastMessageDate: string | null;
  version: number;
  userId?: string; // 🔒 SECURITY: Track which user this cache belongs to
}

interface CacheMetadata {
  totalSize: number;
  chatCount: number;
  lastCleanup: string;
}

/**
 * WhatsApp-style message caching service for instant chat loading
 * Stores messages in localStorage with incremental sync capabilities
 * 🚀 AUGMENT: Enhanced with instant memory cache for zero-delay access
 */
export class ChatMessageCache {
  private static readonly CACHE_PREFIX = 'blissnet_chat_';
  private static readonly METADATA_KEY = 'blissnet_chat_metadata_';
  private static readonly VERSION = 1;
  private static readonly MAX_CACHE_SIZE = 50 * 1024 * 1024; // 50MB limit
  private static readonly MAX_MESSAGES_PER_CHAT = 1000; // Keep last 1000 messages per chat
  private static readonly CLEANUP_INTERVAL = 7 * 24 * 60 * 60 * 1000; // 7 days

  // 🚀 AUGMENT INSTANT MEMORY CACHE: For zero-delay message access
  private static memoryCache = new Map<string, ChatMessage[]>();
  private static memoryCacheMetadata = new Map<string, { lastSync: string; userId: string }>();

  /**
   * 🔒 SECURITY: Get user-specific storage key for a specific chat
   */
  private static getStorageKey(chatId: string, userId?: string): string {
    if (userId) {
      return `${this.CACHE_PREFIX}${userId}_${chatId}`;
    }
    // Fallback for backward compatibility (will be migrated)
    return `${this.CACHE_PREFIX}${chatId}`;
  }

  /**
   * 🚀 AUGMENT: Get consistent memory cache key
   */
  private static getMemoryKey(chatId: string, userId: string): string {
    return `${chatId}_${userId}`;
  }

  /**
   * 🔒 SECURITY: Get user-specific metadata key
   */
  private static getMetadataKey(userId: string): string {
    return `${this.METADATA_KEY}${userId}`;
  }

  /**
   * 🚀 AUGMENT: Load messages instantly from memory cache, fallback to localStorage
   */
  static getMessages(chatId: string, userId?: string): ChatMessage[] {
    try {
      // 🚀 AUGMENT: Try memory cache first for instant access
      if (userId) {
        const memoryCacheKey = this.getMemoryKey(chatId, userId);
        const memoryMessages = this.memoryCache.get(memoryCacheKey);
        const memoryMeta = this.memoryCacheMetadata.get(memoryCacheKey);

        if (memoryMessages && memoryMeta && memoryMeta.userId === userId) {
          if (import.meta.env.DEV) {
            logger.debugThrottled(`memory-cache-hit-${chatId}`, `🚀 MEMORY CACHE HIT: ${memoryMessages.length} messages for chat ${chatId}`);
          }
          return memoryMessages;
        }
      }

      // Fallback to localStorage cache
      if (userId) {
        const userSpecificKey = this.getStorageKey(chatId, userId);
        const stored = localStorage.getItem(userSpecificKey);
        if (stored) {
          const cached: CachedChatData = JSON.parse(stored);

          // Version check - clear if outdated
          if (cached.version !== this.VERSION) {
            this.clearChat(chatId, userId);
            return [];
          }

          // 🚀 AUGMENT: Populate memory cache for next instant access
          const memoryCacheKey = this.getMemoryKey(chatId, userId);
          this.memoryCache.set(memoryCacheKey, cached.messages || []);
          this.memoryCacheMetadata.set(memoryCacheKey, {
            lastSync: cached.lastSync,
            userId
          });

          if (import.meta.env.DEV) {
            logger.debugThrottled(`storage-cache-hit-${chatId}`, `🚀 STORAGE CACHE HIT: ${cached.messages?.length || 0} messages for chat ${chatId}`);
          }
          return cached.messages || [];
        } else {
          // 🚨 V99.10: Reduced logging - only log cache miss once per session
          if (import.meta.env.DEV) {
            logger.debugThrottled(`cache-miss-${chatId}`, `🔍 CACHE MISS: No cached messages for chat ${chatId}`);
          }
        }
      }

      // Fallback to legacy cache (for migration)
      const legacyKey = this.getStorageKey(chatId);
      const legacyStored = localStorage.getItem(legacyKey);
      if (legacyStored) {
        console.warn(`⚠️ CHAT-MSG-CACHE: Using legacy cache for chat ${chatId} - should migrate to user-specific`);
        const cached: CachedChatData = JSON.parse(legacyStored);

        // Version check - clear if outdated
        if (cached.version !== this.VERSION) {
          this.clearChat(chatId);
          return [];
        }

        // If we have a userId, migrate to user-specific cache
        if (userId) {
          this.saveMessages(chatId, cached.messages || [], userId);
          localStorage.removeItem(legacyKey); // Remove legacy cache
          if (import.meta.env.DEV) {
            logger.debugThrottled(`migration-${chatId}`, `🔄 CHAT-MSG-CACHE: Migrated legacy cache to user-specific for chat ${chatId}`);
          }
        }

        return cached.messages || [];
      }

      if (import.meta.env.DEV) {
        logger.debugThrottled(`no-cache-${chatId}`, `❌ CACHE MISS: No cached messages found for chat ${chatId}`);
      }
      return [];
    } catch (error) {
      console.error('Failed to load cached messages:', error);
      this.clearChat(chatId, userId);
      return [];
    }
  }

  /**
   * 🚀 AUGMENT: Save messages to both memory cache and localStorage for instant access
   */
  static saveMessages(chatId: string, messages: ChatMessage[], userId?: string): void {
    if (!userId) {
      console.error('❌ CHAT-MSG-CACHE: Cannot save messages without user ID');
      return;
    }

    try {
      // 🚀 V167.6: CRITICAL FIX - Deduplicate messages before caching to prevent duplicate first messages
      // This is a safety net to ensure we never cache duplicates, even if caller doesn't deduplicate
      const deduplicatedMessages = Array.from(
        new Map(messages.map(msg => [msg.id, msg])).values()
      );

      // Limit messages to prevent excessive storage usage
      const limitedMessages = deduplicatedMessages.slice(-this.MAX_MESSAGES_PER_CHAT);

      // 🚀 AUGMENT: Save to memory cache first for instant access
      const memoryCacheKey = this.getMemoryKey(chatId, userId);
      this.memoryCache.set(memoryCacheKey, limitedMessages);
      if (import.meta.env.DEV) {
        console.log(`💾 CACHE SAVE: ${limitedMessages.length} messages for chat ${chatId}, user ${userId}`);
      }
      this.memoryCacheMetadata.set(memoryCacheKey, {
        lastSync: new Date().toISOString(),
        userId
      });

      const lastMessageDate = limitedMessages.length > 0
        ? limitedMessages[limitedMessages.length - 1].created_at
        : null;

      const cacheData: CachedChatData = {
        messages: limitedMessages,
        lastSync: new Date().toISOString(),
        lastMessageDate,
        version: this.VERSION,
        userId // 🔒 SECURITY: Store user ID for validation
      };

      const storageKey = this.getStorageKey(chatId, userId);
      if (import.meta.env.DEV) {
        console.log(`💾 STORAGE SAVE: Key "${storageKey}" for chat ${chatId}, user ${userId}`);
      }
      localStorage.setItem(storageKey, JSON.stringify(cacheData));
      this.updateMetadata(userId);

      // Messages saved to cache
    } catch (error) {
      console.error('Failed to save messages to cache:', error);
      // If storage is full, try cleanup and retry
      this.performCleanup(userId);
      try {
        const cacheData: CachedChatData = {
          messages: messages.slice(-this.MAX_MESSAGES_PER_CHAT),
          lastSync: new Date().toISOString(),
          lastMessageDate: messages.length > 0 ? messages[messages.length - 1].created_at : null,
          version: this.VERSION,
          userId
        };
        const storageKey = this.getStorageKey(chatId, userId);
        localStorage.setItem(storageKey, JSON.stringify(cacheData));
        if (import.meta.env.DEV) {
        console.log(`💾 CHAT-MSG-CACHE: Saved ${messages.length} messages for chat ${chatId} (user: ${userId}) after cleanup`);
      }
      } catch (retryError) {
        console.warn('Failed to save messages even after cleanup:', retryError);
      }
    }
  }

  /**
   * 🔒 SECURITY: Add a single new message to user-specific cache
   */
  static addMessage(chatId: string, message: ChatMessage, userId?: string): void {
    if (!userId) {
      console.error('Cannot add message without user ID');
      return;
    }

    const cached = this.getMessages(chatId, userId);

    // Check for duplicates
    if (cached.some(msg => msg.id === message.id)) {
      return;
    }

    cached.push(message);
    this.saveMessages(chatId, cached, userId);
  }

  /**
   * 🔒 SECURITY: Update an existing message in user-specific cache (for status updates)
   */
  static updateMessage(chatId: string, messageId: string, updates: Partial<ChatMessage>, userId?: string): void {
    if (!userId) {
      console.error('❌ CHAT-MSG-CACHE: Cannot update message without user ID');
      return;
    }

    const cached = this.getMessages(chatId, userId);
    const messageIndex = cached.findIndex(msg => msg.id === messageId);

    if (messageIndex >= 0) {
      cached[messageIndex] = { ...cached[messageIndex], ...updates };
      this.saveMessages(chatId, cached, userId);
    }
  }

  /**
   * 🔒 SECURITY: Get the date of the last cached message for incremental sync (user-specific)
   */
  static getLastMessageDate(chatId: string, userId?: string): Date | null {
    try {
      // Try user-specific cache first
      if (userId) {
        const userSpecificKey = this.getStorageKey(chatId, userId);
        const stored = localStorage.getItem(userSpecificKey);
        if (stored) {
          const cached: CachedChatData = JSON.parse(stored);
          return cached.lastMessageDate ? new Date(cached.lastMessageDate) : null;
        }
      }

      // Fallback to legacy cache
      const legacyKey = this.getStorageKey(chatId);
      const legacyStored = localStorage.getItem(legacyKey);
      if (legacyStored) {
        const cached: CachedChatData = JSON.parse(legacyStored);
        return cached.lastMessageDate ? new Date(cached.lastMessageDate) : null;
      }

      return null;
    } catch (error) {
      console.error('Failed to get last message date:', error);
      return null;
    }
  }

  /**
   * 🔒 SECURITY: Check if chat has cached messages (user-specific)
   */
  static hasCache(chatId: string, userId?: string): boolean {
    if (userId) {
      const userSpecificKey = this.getStorageKey(chatId, userId);
      if (localStorage.getItem(userSpecificKey) !== null) {
        return true;
      }
    }

    // Check legacy cache
    return localStorage.getItem(this.getStorageKey(chatId)) !== null;
  }

  /**
   * 🔒 SECURITY: Clear cache for a specific chat (user-specific)
   */
  static clearChat(chatId: string, userId?: string): void {
    if (userId) {
      const userSpecificKey = this.getStorageKey(chatId, userId);
      localStorage.removeItem(userSpecificKey);
      if (import.meta.env.DEV) {
      console.log(`🗑️ CHAT-MSG-CACHE: Cleared chat ${chatId} for user ${userId}`);
    }
    }

    // Also clear legacy cache if it exists
    const legacyKey = this.getStorageKey(chatId);
    if (localStorage.getItem(legacyKey)) {
      localStorage.removeItem(legacyKey);
      if (import.meta.env.DEV) {
        console.log(`🗑️ CHAT-MSG-CACHE: Cleared legacy cache for chat ${chatId}`);
      }
    }

    if (userId) {
      this.updateMetadata(userId);
    }
  }

  /**
   * 🔒 SECURITY: Clear all chat caches for a specific user
   */
  static clearAllForUser(userId: string): void {
    if (!userId) {
      console.error('❌ CHAT-MSG-CACHE: Cannot clear user cache without user ID');
      return;
    }

    const keys = Object.keys(localStorage);
    const userCacheKeys = keys.filter(key =>
      key.startsWith(this.CACHE_PREFIX) && key.includes(`_${userId}_`)
    );

    userCacheKeys.forEach(key => {
      localStorage.removeItem(key);
    });

    // Clear user-specific metadata
    const metadataKey = this.getMetadataKey(userId);
    localStorage.removeItem(metadataKey);

    if (import.meta.env.DEV) {
      console.log(`🗑️ CHAT-MSG-CACHE: Cleared ${userCacheKeys.length} caches for user ${userId}`);
    }
  }

  /**
   * 🧹 SECURITY: Clear ALL chat caches (for complete cleanup/logout)
   */
  static clearAll(): void {
    const keys = Object.keys(localStorage);
    const chatCacheKeys = keys.filter(key =>
      key.startsWith(this.CACHE_PREFIX) || key.startsWith(this.METADATA_KEY)
    );

    chatCacheKeys.forEach(key => {
      localStorage.removeItem(key);
    });

    if (import.meta.env.DEV) {
      console.log(`🗑️ CHAT-MSG-CACHE: Cleared ${chatCacheKeys.length} total cache entries`);
    }
  }

  /**
   * 🚨 EMERGENCY: Clear contaminated global cache and migrate to user-specific
   */
  static clearContaminatedCache(): void {
    console.warn('🚨 CHAT-MSG-CACHE: Clearing contaminated global cache...');

    const keys = Object.keys(localStorage);
    let clearedCount = 0;

    // Clear old global cache entries that don't have user IDs in the key
    keys.forEach(key => {
      if (key.startsWith(this.CACHE_PREFIX) && !key.includes('_') ||
          key.includes('_') && key.split('_').length < 3) {
        // This is likely a legacy global cache entry
        localStorage.removeItem(key);
        clearedCount++;
      }
    });

    // Clear old metadata
    if (localStorage.getItem('blissnet_chat_metadata')) {
      localStorage.removeItem('blissnet_chat_metadata');
      clearedCount++;
    }

    // Clear other potentially contaminated cache
    if (localStorage.getItem('blissnet_chat_conversations')) {
      localStorage.removeItem('blissnet_chat_conversations');
      clearedCount++;
    }

    console.warn(`🧹 CHAT-MSG-CACHE: Cleared ${clearedCount} contaminated cache entries`);
  }

  /**
   * Preload messages for a conversation
   */
  static preloadMessages(conversationId: string, userId?: string): void {
    try {
      const messages = this.getMessages(conversationId, userId);
      if (import.meta.env.DEV) {
        logger.debug(`Preloaded ${messages.length} messages for conversation ${conversationId}`);
      }
    } catch (error) {
      logger.error('Failed to preload messages:', error);
    }
  }

  /**
   * Get unread count for a conversation
   */
  static getUnreadCount(conversationId: string, userId?: string): number {
    try {
      // For now, return 0 as we don't track read status in cache
      // This could be enhanced to track last read message timestamp
      return 0;
    } catch (error) {
      logger.error('Failed to get unread count:', error);
      return 0;
    }
  }

  /**
   * Get cache statistics (global)
   */
  static getCacheStats(): { totalChats: number; totalSize: number; oldestCache: Date | null } {
    const keys = Object.keys(localStorage);
    const chatKeys = keys.filter(key => key.startsWith(this.CACHE_PREFIX));

    let totalSize = 0;
    let oldestCache: Date | null = null;

    chatKeys.forEach(key => {
      const value = localStorage.getItem(key);
      if (value) {
        totalSize += value.length;
        try {
          const cached: CachedChatData = JSON.parse(value);
          const syncDate = new Date(cached.lastSync);
          if (!oldestCache || syncDate < oldestCache) {
            oldestCache = syncDate;
          }
        } catch (error) {
          // Ignore invalid cache entries
        }
      }
    });

    return {
      totalChats: chatKeys.length,
      totalSize,
      oldestCache
    };
  }

  /**
   * 🔒 SECURITY: Get cache statistics for a specific user
   */
  static getCacheStatsForUser(userId: string): { totalChats: number; totalSize: number; oldestCache: Date | null } {
    const keys = Object.keys(localStorage);
    const userChatKeys = keys.filter(key =>
      key.startsWith(this.CACHE_PREFIX) && key.includes(`_${userId}_`)
    );

    let totalSize = 0;
    let oldestCache: Date | null = null;

    userChatKeys.forEach(key => {
      const value = localStorage.getItem(key);
      if (value) {
        totalSize += value.length;
        try {
          const cached: CachedChatData = JSON.parse(value);
          const syncDate = new Date(cached.lastSync);
          if (!oldestCache || syncDate < oldestCache) {
            oldestCache = syncDate;
          }
        } catch (error) {
          // Ignore invalid cache entries
        }
      }
    });

    return {
      totalChats: userChatKeys.length,
      totalSize,
      oldestCache
    };
  }

  /**
   * 🔒 SECURITY: Update user-specific cache metadata
   */
  private static updateMetadata(userId?: string): void {
    if (!userId) {
      console.warn('Cannot update metadata without user ID');
      return;
    }

    try {
      const stats = this.getCacheStatsForUser(userId);
      const metadata: CacheMetadata = {
        totalSize: stats.totalSize,
        chatCount: stats.totalChats,
        lastCleanup: new Date().toISOString()
      };
      const metadataKey = this.getMetadataKey(userId);
      localStorage.setItem(metadataKey, JSON.stringify(metadata));
    } catch (error) {
      console.warn('Failed to update cache metadata:', error);
    }
  }

  /**
   * 🔒 SECURITY: Perform user-specific cache cleanup to free space
   */
  private static performCleanup(userId?: string): void {
    if (!userId) {
      console.warn('⚠️ CHAT-MSG-CACHE: Cannot perform cleanup without user ID');
      return;
    }

    try {
      const stats = this.getCacheStatsForUser(userId);

      // If we're over the size limit, remove oldest caches for this user
      if (stats.totalSize > this.MAX_CACHE_SIZE / 10) { // Per-user limit is 1/10th of total
        const keys = Object.keys(localStorage);
        const userChatEntries: Array<{ key: string; lastSync: Date }> = [];

        keys.forEach(key => {
          if (key.startsWith(this.CACHE_PREFIX) && key.includes(`_${userId}_`)) {
            const value = localStorage.getItem(key);
            if (value) {
              try {
                const cached: CachedChatData = JSON.parse(value);
                userChatEntries.push({
                  key,
                  lastSync: new Date(cached.lastSync)
                });
              } catch (error) {
                // Remove invalid entries
                localStorage.removeItem(key);
              }
            }
          }
        });

        // Sort by last sync date (oldest first)
        userChatEntries.sort((a, b) => a.lastSync.getTime() - b.lastSync.getTime());

        // Remove oldest 25% of user's caches
        const toRemove = Math.ceil(userChatEntries.length * 0.25);
        for (let i = 0; i < toRemove; i++) {
          localStorage.removeItem(userChatEntries[i].key);
        }

        if (import.meta.env.DEV) {
      console.log(`🧹 CHAT-MSG-CACHE: Cleanup removed ${toRemove} old chat caches for user ${userId}`);
    }
      }
    } catch (error) {
      console.error('Failed to perform cache cleanup:', error);
    }
  }

  /**
   * Initialize cache system (call on app startup)
   */
  static initialize(): void {
    try {
      const metadata = localStorage.getItem(this.METADATA_KEY);
      if (metadata) {
        const meta: CacheMetadata = JSON.parse(metadata);
        const lastCleanup = new Date(meta.lastCleanup);
        const now = new Date();
        
        // Perform cleanup if it's been more than a week
        if (now.getTime() - lastCleanup.getTime() > this.CLEANUP_INTERVAL) {
          this.performCleanup();
        }
      }
      // Note: Skip metadata update during initialization since we don't have user context
      // Metadata will be updated when messages are first saved/loaded with user ID
      
      // Add debug function to window for testing
      (window as any).debugChatCache = {
        getMessages: (chatId: string, userId: string) => {
          if (import.meta.env.DEV) {
      console.log(`🔍 DEBUG: Getting messages for chat ${chatId}, user ${userId}`);
    }
          const messages = ChatMessageCache.getMessages(chatId, userId);
                      if (import.meta.env.DEV) {
              console.log(`📊 DEBUG: Found ${messages.length} messages`);
            }
          return messages;
        },
        saveMessages: (chatId: string, messages: any[], userId: string) => {
                      if (import.meta.env.DEV) {
              console.log(`💾 DEBUG: Saving ${messages.length} messages for chat ${chatId}, user ${userId}`);
            }
          ChatMessageCache.saveMessages(chatId, messages, userId);
        },
        clearChat: (chatId: string, userId: string) => {
                      if (import.meta.env.DEV) {
              console.log(`🗑️ DEBUG: Clearing cache for chat ${chatId}, user ${userId}`);
            }
          ChatMessageCache.clearChat(chatId, userId);
        },
        getStats: () => {
                      if (import.meta.env.DEV) {
              console.log('📊 DEBUG: Cache stats');
            }
          return ChatMessageCache.getCacheStats();
        },
        // Add user cache debugging
        getUser: (chatId: string, userId: string) => {
                      if (import.meta.env.DEV) {
              console.log(`🔍 DEBUG: Getting user for chat ${chatId}, user ${userId}`);
            }
          const { EnhancedChatCache } = require('../cache/EnhancedChatCache');
          const cachedUser = EnhancedChatCache.get<any>(
            `user_${chatId}`,
            userId,
            { fallbackToStorage: true, updateAccessStats: false }
          );
                      if (import.meta.env.DEV) {
              console.log(`👤 DEBUG: Found user:`, cachedUser);
            }
          return cachedUser;
        },
        saveUser: (chatId: string, userData: any, userId: string) => {
                      if (import.meta.env.DEV) {
              console.log(`💾 DEBUG: Saving user for chat ${chatId}, user ${userId}:`, userData);
            }
          const { EnhancedChatCache } = require('../cache/EnhancedChatCache');
          EnhancedChatCache.set(`user_${chatId}`, userId, userData, {
            priority: 'critical',
            ttl: 30 * 60 * 1000 // 30 minutes
          });
        }
      };
      
      // 🚀 PRODUCTION OPTIMIZED: Cache initialization only logged once per session
      if (import.meta.env.DEV) {
        logger.debug('🚀 CHAT-MSG-CACHE: Initialized with debug functions (window.debugChatCache)');
      }
    } catch (error) {
      console.error('Failed to initialize cache system:', error);
    }
  }
}

